import { useMemo } from "react";
import type { StripeAddressElementOptions } from "@stripe/stripe-js";
import type { CommunityMemberBillingInfo } from "@/react/types";

export interface UseAddressElementDefaultValuesProps {
  billingInfo?: CommunityMemberBillingInfo;
  name?: string;
}

export const useAddressElementDefaultValues = ({
  billingInfo,
  name,
}: UseAddressElementDefaultValuesProps) =>
  useMemo<StripeAddressElementOptions["defaultValues"]>(() => {
    const defaultValues: StripeAddressElementOptions["defaultValues"] = {};

    // Supplying any `defaultValues` (even just `name`) disables Stripe's IP-based country detection.
    // Only pre-fill when we already know the member's country; otherwise return an empty object so
    // geolocation still works.
    if (!billingInfo || !billingInfo.address_country) {
      return defaultValues;
    }

    // If there is a community member logged in, we prefill the Address Element with their name.
    //
    // Otherwise, we rely on the Address Element to provide our form with the values it requires
    // to sign up the new community member.
    //
    // Our form does not render the name field if the Address Element is rendered.
    defaultValues.name = name;
    defaultValues.address = {
      line1: billingInfo.address_line1,
      line2: billingInfo.address_line2,
      city: billingInfo.address_city,
      state: billingInfo.address_state,
      postal_code: billingInfo.address_postal_code,
      country: billingInfo.address_country,
    };

    return defaultValues;
  }, [billingInfo, name]);
